import React from "react";
import { useTask } from "vig-ui-library";
import { RequestMagicLinkMethod } from "../Enums/RequestMagicLinkMethod";
import { MagicLinkIdCardUploadMethod } from "../Enums/MagicLinkIdCardUploadMethod";
import { RequestFormView } from "../Enums/RequestFormView";
import { GetMagicLinkConfigInputSchema } from "../Schemas/GetMagicLinkConfigInput";
import { GetMagicLinkConfigOutput } from "../Schemas/GetMagicLinkConfigOutput";
import { CreateMagicLinkInputSchema } from "../Schemas/CreateMagicLinkInput";
import { StartMagicLinkProcessInputSchema } from "../Schemas/StartMagicLinkProcessInput";
import { CancelMagicLinkProcessInputSchema } from "../Schemas/CancelMagicLinkProcessInput";
import { UseRequestApi } from "../Api/RequestApi";
import { Request, parseUtcDate } from "../Utilities/mappers";

export interface UseMagicLinkParams {
    requestApi: UseRequestApi;
    request: React.MutableRefObject<Request | null>;
    requestId: React.MutableRefObject<string | undefined>;
    getRequestId: () => string | undefined;
    setView: (view: RequestFormView) => void;
    onlySaveRequestRef: React.MutableRefObject<((debounced?: boolean, shouldReloadRequestData?: boolean, successHandler?: () => void, fetchFieldsRelation?: boolean) => void) | null>;
}

export interface UseMagicLinkReturn {
    // States
    magicLinkUploadMethod: MagicLinkIdCardUploadMethod | null;
    setMagicLinkUploadMethod: (method: MagicLinkIdCardUploadMethod | null) => void;
    magicLinkDeliveryMethod: RequestMagicLinkMethod | null;
    setMagicLinkDeliveryMethod: (method: RequestMagicLinkMethod | null) => void;
    magicLinkRecipient: string | null;
    setMagicLinkRecipient: (recipient: string | null) => void;
    magicLinkError: string | null;
    setMagicLinkError: (error: string | null) => void;
    magicLinkExpireAt: Date | null;
    setMagicLinkExpireAt: (expireAt: Date | null) => void;
    isMagicLinkOpened: boolean;
    magicLinkEmails: string[];
    setMagicLinkEmails: (emails: string[]) => void;
    magicLinkPhones: string[];
    setMagicLinkPhones: (phones: string[]) => void;
    isIdCardUploadRequiredForRequest: boolean;
    setIsIdCardUploadRequiredForRequest: (required: boolean) => void;

    // Refs
    idCardFrontTaxonomyId: React.MutableRefObject<number | null>;
    idCardBackTaxonomyId: React.MutableRefObject<number | null>;

    // Helper functions
    getIdCardFrontFileId: () => string | null;
    getIdCardBackFileId: () => string | null;

    // Tasks
    loadMagicLinkConfigTask: ReturnType<typeof useTask<[requestId?: string, requestDetail?: { magicLinkSendMethod?: RequestMagicLinkMethod | null, magicLinkRecipient?: string | null }], GetMagicLinkConfigOutput | null>>;
    startMagicLinkProcessTask: ReturnType<typeof useTask<[], void>>;
    cancelMagicLinkProcessTask: ReturnType<typeof useTask<[], void>>;
    createMagicLinkTask: ReturnType<typeof useTask<[options?: { recipient: string; method: RequestMagicLinkMethod }], void>>;
}

/**
 * Picks a recipient that is guaranteed to exist in the given contacts.
 *
 * The available contacts depend on the partner/contract selected in the form, which the user can
 * change after a method and recipient were already chosen. Sending a recipient that no longer
 * belongs to the current partner is rejected by the backend, so a stale candidate is replaced by
 * the first available contact. Phones are compared without spaces and "+" to match the backend.
 */
const resolveAvailableRecipient = (
    method: RequestMagicLinkMethod,
    candidate: string | null | undefined,
    emails: string[],
    phones: string[]
): string | null => {
    const isSms = method === RequestMagicLinkMethod.Sms;
    const available = isSms ? phones : emails;

    if (available.length === 0) {
        return null;
    }

    if (!candidate) {
        return available[0];
    }

    const normalize = (value: string) => isSms
        ? value.replace(/[\s+]/g, "")
        : value.trim().toLowerCase();

    return available.find(contact => normalize(contact) === normalize(candidate)) ?? available[0];
};

/**
 * Custom hook for managing magic link feature state and operations.
 * 
 * This hook encapsulates all magic link-related functionality including:
 * - State management for delivery methods, recipients, errors, and expiration
 * - Configuration loading and validation
 * - Magic link process initialization and creation
 * - ID card upload requirements and file management
 */
export const useMagicLink = (params: UseMagicLinkParams): UseMagicLinkReturn => {
    const { requestApi, request, getRequestId, setView, onlySaveRequestRef } = params;

    // State for magic link upload method (who uploads the ID card)
    const [magicLinkUploadMethod, setMagicLinkUploadMethod] = React.useState<MagicLinkIdCardUploadMethod | null>(
        MagicLinkIdCardUploadMethod.You
    );

    // State for delivery method (SMS or Email)
    const [magicLinkDeliveryMethod, _setMagicLinkDeliveryMethod] = React.useState<RequestMagicLinkMethod | null>(
        RequestMagicLinkMethod.Sms
    );

    // State for recipient (phone number or email address)
    const [magicLinkRecipient, setMagicLinkRecipient] = React.useState<string | null>(null);

    // State for error messages
    const [magicLinkError, setMagicLinkError] = React.useState<string | null>(null);

    // State for link expiration date
    const [magicLinkExpireAt, setMagicLinkExpireAt] = React.useState<Date | null>(null);

    // State to track if the link has been opened (session started)
    const [isMagicLinkOpened, setIsMagicLinkOpened] = React.useState<boolean>(false);

    // State for available email addresses
    const [magicLinkEmails, setMagicLinkEmails] = React.useState<string[]>([]);

    // State for available phone numbers
    const [magicLinkPhones, setMagicLinkPhones] = React.useState<string[]>([]);

    // State for ID card upload requirement
    const [isIdCardUploadRequiredForRequest, setIsIdCardUploadRequiredForRequest] = React.useState<boolean>(false);

    // Refs for taxonomy IDs used to identify ID card files
    const idCardFrontTaxonomyId = React.useRef<number | null>(null);
    const idCardBackTaxonomyId = React.useRef<number | null>(null);

    /**
     * Getter function for front ID card file ID.
     * Searches the request files for a file matching the front taxonomy ID.
     */
    const getIdCardFrontFileId = React.useCallback((): string | null => {
        if (!idCardFrontTaxonomyId.current || !request.current?.files) {
            return null;
        }
        const frontFile = request.current.files.find(
            file => file.taxonomyId === idCardFrontTaxonomyId.current && file.id
        );
        return frontFile?.id ?? null;
    }, []);

    /**
     * Getter function for back ID card file ID.
     * Searches the request files for a file matching the back taxonomy ID.
     */
    const getIdCardBackFileId = React.useCallback((): string | null => {
        if (!idCardBackTaxonomyId.current || !request.current?.files) {
            return null;
        }
        const backFile = request.current.files.find(
            file => file.taxonomyId === idCardBackTaxonomyId.current && file.id
        );
        return backFile?.id ?? null;
    }, []);

    /**
     * Sets the delivery method and automatically updates the recipient
     * based on available emails/phones for the selected method.
     */
    const setMagicLinkDeliveryMethod = React.useCallback((method: RequestMagicLinkMethod | null) => {
        _setMagicLinkDeliveryMethod(method);
        // Update recipient based on the new method
        if (method === RequestMagicLinkMethod.Sms) {
            setMagicLinkRecipient(magicLinkPhones.length > 0 ? magicLinkPhones[0] : null);
        } else if (method === RequestMagicLinkMethod.Email) {
            setMagicLinkRecipient(magicLinkEmails.length > 0 ? magicLinkEmails[0] : null);
        }
    }, [magicLinkPhones, magicLinkEmails]);

    /**
     * Task: Loads magic link configuration for a request.
     * 
     * Fetches available emails/phones, ID card requirements, and the latest magic link.
     * Updates all related state including delivery method and recipient based on:
     * 1. Existing unexpired magic link (if available)
     * 2. Stored method/recipient from request detail (if no link exists)
     * 3. Default selection from available contacts
     */
    const loadMagicLinkConfigTask = useTask(async (
        requestId?: string,
        requestDetail?: { magicLinkSendMethod?: RequestMagicLinkMethod | null, magicLinkRecipient?: string | null }
    ): Promise<GetMagicLinkConfigOutput | null> => {
        try {
            const input = GetMagicLinkConfigInputSchema.parse({
                requestId: requestId || request.current?.requestId,
            });
            const result = await requestApi.getMagicLinkConfig(input);
            const emails = result.emails || [];
            const phones = result.phones || [];
            const isIdCardUploadRequired = result.isIdCardUploadRequiredForRequest || false;

            setMagicLinkEmails(emails);
            setMagicLinkPhones(phones);
            setIsIdCardUploadRequiredForRequest(isIdCardUploadRequired);
            idCardFrontTaxonomyId.current = result.idCardFrontTaxonomyId ?? null;
            idCardBackTaxonomyId.current = result.idCardBackTaxonomyId ?? null;

            // Check if we have at least one email or phone available
            if (emails.length === 0 && phones.length === 0) {
                setMagicLinkError("Nie sú k dispozícii žiadne emailové adresy ani telefónne čísla pre odoslanie Magic Linku.");
                setMagicLinkRecipient(null);
                return result;
            }

            // Clear any previous errors
            setMagicLinkError(null);

            // The recipient is always resolved against the contacts just loaded, so a method and a
            // recipient belonging to different partners can never end up in the state together.
            // The raw setter is used because the wrapper would resolve against the previous contacts.
            if (result.latestMagicLink) {
                // If a magic link exists, set the link details (even if expired, so view can display expired state)
                const latestLinkOutput = result.latestMagicLink;
                const link = latestLinkOutput.link;
                // Use computed expiry from backend (handles CancelledAt, SessionExpireAt, and ExpireAt)
                const expirationDate = parseUtcDate(latestLinkOutput.computedExpiryAt);
                setMagicLinkExpireAt(expirationDate);
                _setMagicLinkDeliveryMethod(link.method);
                setMagicLinkRecipient(resolveAvailableRecipient(link.method, link.recipient, emails, phones));
                // Check if link has been opened (session started) - sessionStartedAt is set when link is opened
                setIsMagicLinkOpened(link.sessionStartedAt != null);
            } else if (requestDetail?.magicLinkSendMethod != null && requestDetail?.magicLinkRecipient) {
                // No link exists yet, but we have stored method and recipient from request detail
                _setMagicLinkDeliveryMethod(requestDetail.magicLinkSendMethod);
                setMagicLinkRecipient(resolveAvailableRecipient(requestDetail.magicLinkSendMethod, requestDetail.magicLinkRecipient, emails, phones));
            } else {
                // Keep the currently selected method and only re-resolve its recipient
                const method = magicLinkDeliveryMethod ?? RequestMagicLinkMethod.Sms;

                if (method === RequestMagicLinkMethod.Sms && phones.length === 0) {
                    setMagicLinkError("Nie sú k dispozícii žiadne telefónne čísla pre odoslanie Magic Linku.");
                    setMagicLinkRecipient(null);
                    return result;
                }

                if (method === RequestMagicLinkMethod.Email && emails.length === 0) {
                    setMagicLinkError("Nie sú k dispozícii žiadne emailové adresy pre odoslanie Magic Linku.");
                    setMagicLinkRecipient(null);
                    return result;
                }

                setMagicLinkRecipient(resolveAvailableRecipient(method, magicLinkRecipient, emails, phones));
            }

            return result;
        } catch (err) {
            console.log(err);
            setMagicLinkError("Nepodarilo sa načítať konfiguráciu Magic Linku.");
            return null;
        }
    });

    /**
     * Task: Starts the magic link signing process for a request.
     * 
     * Initializes the magic link process by setting MagicLinkStartedAt,
     * MagicLinkSendMethod, and MagicLinkRecipient on the ExternalRequest.
     * This must be called before creating a magic link.
     */
    const startMagicLinkProcessTask = useTask(async () => {
        try {
            const currentRequestId = getRequestId();
            if (!currentRequestId) {
                throw new Error("Request ID is required");
            }

            if (!magicLinkRecipient) {
                throw new Error("Recipient is required");
            }

            const method = magicLinkDeliveryMethod ?? RequestMagicLinkMethod.Sms;

            const input = StartMagicLinkProcessInputSchema.parse({
                requestId: currentRequestId,
                method: method,
                recipient: magicLinkRecipient,
            });

            const result = await requestApi.startMagicLinkProcess(input);

            if (result.errorMessage) {
                setMagicLinkError(result.errorMessage);
                throw new Error(result.errorMessage);
            }

            setMagicLinkError(null);
        } catch (error: any) {
            const errorMessage = error?.message || error?.response?.data?.errorMessage || "Nepodarilo sa spustiť proces Magic Linku";
            setMagicLinkError(errorMessage);
            throw error;
        }
    });

    /**
     * Task: Cancels the magic link signing process for a request.
     * 
     * Clears MagicLinkStartedAt, MagicLinkSendMethod, and MagicLinkRecipient
     * from the ExternalRequest, effectively resetting the magic link process.
     */
    const cancelMagicLinkProcessTask = useTask(async () => {
        try {
            const currentRequestId = getRequestId();
            if (!currentRequestId) {
                throw new Error("Request ID is required");
            }

            const input = CancelMagicLinkProcessInputSchema.parse({
                requestId: currentRequestId,
            });

            const result = await requestApi.cancelMagicLinkProcess(input);

            if (result.errorMessage) {
                setMagicLinkError(result.errorMessage);
                throw new Error(result.errorMessage);
            }

            // Clear local state
            // Note: We keep magicLinkDeliveryMethod and magicLinkRecipient so they remain in the dropdown
            // when user goes back to signature method selection. Only clear the link-specific state.
            setMagicLinkError(null);
            setMagicLinkExpireAt(null);
            setIsMagicLinkOpened(false);
            // Don't clear magicLinkDeliveryMethod and magicLinkRecipient - keep them for UI continuity

            // Save request to sync state after canceling (backend may have deleted ID card files if not required by default)
            // This ensures the frontend request object is in sync with backend
            // We do this as part of the cancel process so navigation only happens after both cancel and save complete
            if (onlySaveRequestRef.current) {
                await new Promise<void>((resolve) => {
                    onlySaveRequestRef.current!(false, true, () => resolve());
                });
            }
        } catch (error: any) {
            const errorMessage = error?.message || error?.response?.data?.errorMessage || "Nepodarilo sa zrušiť proces Magic Linku";
            setMagicLinkError(errorMessage);
            throw error;
        }
    });

    /**
     * Task: Creates and sends a magic link to the recipient.
     * 
     * Creates a new magic link, sends it via the specified method (SMS/Email),
     * and navigates to the magic link sent view upon success.
     * Handles ID card upload requirements if needed.
     * When options (recipient, method) are passed (e.g. from resend modal), they are used for the API call and state is synced after success.
     */
    const createMagicLinkTask = useTask(async (options?: { recipient: string; method: RequestMagicLinkMethod }) => {
        try {
            const currentRequestId = getRequestId();
            if (!currentRequestId) {
                throw new Error("Request ID is required");
            }

            const recipient = options?.recipient ?? magicLinkRecipient;
            const method = options?.method ?? magicLinkDeliveryMethod ?? RequestMagicLinkMethod.Sms;

            if (!recipient) {
                throw new Error("Recipient is required");
            }

            const input = CreateMagicLinkInputSchema.parse({
                requestId: currentRequestId,
                method,
                recipient,
            });

            const result = await requestApi.createMagicLink(input);

            if (result.errorMessage) {
                setMagicLinkError(result.errorMessage);
                throw new Error(result.errorMessage);
            }

            // Sync state when overrides were used (e.g. from resend modal) so UI shows the sent recipient/method
            if (options) {
                setMagicLinkRecipient(options.recipient);
                setMagicLinkDeliveryMethod(options.method);
            }

            // Parse expireAt from ISO string as UTC
            const expireAt = parseUtcDate(result.expireAt);
            setMagicLinkExpireAt(expireAt);
            setMagicLinkError(null);
            setView(RequestFormView.MAGIC_LINK);
        } catch (error: any) {
            const errorMessage = error?.message || error?.response?.data?.errorMessage || "Nepodarilo sa odoslať Magic Link";
            setMagicLinkError(errorMessage);
            throw error;
        }
    });

    return {
        // States
        magicLinkUploadMethod,
        setMagicLinkUploadMethod,
        magicLinkDeliveryMethod,
        setMagicLinkDeliveryMethod,
        magicLinkRecipient,
        setMagicLinkRecipient,
        magicLinkError,
        setMagicLinkError,
        magicLinkExpireAt,
        setMagicLinkExpireAt,
        isMagicLinkOpened,
        magicLinkEmails,
        setMagicLinkEmails,
        magicLinkPhones,
        setMagicLinkPhones,
        isIdCardUploadRequiredForRequest,
        setIsIdCardUploadRequiredForRequest,

        // Refs
        idCardFrontTaxonomyId,
        idCardBackTaxonomyId,

        // Helper functions
        getIdCardFrontFileId,
        getIdCardBackFileId,

        // Tasks
        loadMagicLinkConfigTask,
        startMagicLinkProcessTask,
        cancelMagicLinkProcessTask,
        createMagicLinkTask,
    };
};

